/*
 * Copyright (C) 2018 Robert Bosch GmbH Copyright (C) 2018 Robert Bosch Manufacturing Solutions GmbH, Germany. All rights reserved.
 */

import { OverlayModule } from "@angular/cdk/overlay";
import ptBr from "@angular/common/locales/pt";
import { registerLocaleData } from "@angular/common";
import { CommonModule } from "@angular/common";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatBadgeModule } from "@angular/material/badge";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatChipsModule } from "@angular/material/chips";
import { MatDialogModule } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";
import { MatMenuModule } from "@angular/material/menu";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivityIndicatorDirective } from "./activity-indicator/activity-indicator.directive";
import { BciChipComponent } from "./chip/chip.component";
import { CommandbarActionsComponent } from "./commandbar/actions/actions.component";
import { CommandbarActiveFiltersComponent } from "./commandbar/active-filters/active-filters.component";
import { CommandbarButtonComponent } from "./commandbar/button/commandbar-button.component";
import { ClickOutsideDirective } from "./commandbar/click-outside.directive";
import { CommandbarButtonsDirective } from "./commandbar/commandbar-buttons.directive";
import { CommandbarComponent } from "./commandbar/commandbar.component";
import { CommandbarInlinefilterComponent } from "./commandbar/inlinefilter/commandbar-inlinefilter.component";
import { CommandbarInlinefiltersComponent } from "./commandbar/inlinefilters/commandbar-inlinefilters.component";
import { CommandbarItemComponent } from "./commandbar/item/commandbar-item.component";
import {
  COMMANDBAR_OPTIONSMENU_DATA,
  CommandbarOptionsmenuComponent
} from "./commandbar/optionsmenu/commandbar-optionsmenu.component";
import { OptionsmenuDialogComponent } from "./commandbar/optionsmenu/optionsmenu-dialog/optionsmenu-dialog.component";
import { CommandbarQuickfiltersComponent } from "./commandbar/quickfilters/commandbar-quickfilters.component";
import { CommandbarSidebarRoutingNavigatorDirective } from "./commandbar/sidebar/commandbar-sidebar-routing-navigator.directive";
import {
  CommandbarSidebarSlideComponent,
  CommandbarSidebarSlideFooterDirective,
  CommandbarSidebarSlideHeaderDirective
} from "./commandbar/sidebar/commandbar-sidebar-slide.component";
import { CommandbarSidebarComponent } from "./commandbar/sidebar/commandbar-sidebar.component";
import { ToggleButtonDirective } from "./commandbar/toggle-button.directive";
import { ContentSidebarItemComponent } from "./content-sidebar-item/content-sidebar-item.component";
import { ContentSidebarComponent } from "./content-sidebar/content-sidebar.component";
import { ContentWSidePanelComponent } from "./content-w-side-panel/content-w-side-panel.component";
import { FilterPanelComponent } from "./filter-panel/filter-panel.component";
import { FilterEntryComponent } from "./filter-picker/filter-entry/filter-entry.component";
import { FilterPickerComponent } from "./filter-picker/filter-picker.component";
import { ClickableItemDirective } from "./interaction/clickable-item.directive";
import { LoadingSpinnerDirective } from "./loading-spinner/loading-spinner.directive";
import { ListSkeletonComponent } from "./loading-text-skeleton/list-skeleton/list-skeleton.component";
import { LoadingTextSkeletonDirective } from "./loading-text-skeleton/loading-text-skeleton.directive";
import { TableSkeletonComponent } from "./loading-text-skeleton/table-skeleton/table-skeleton.component";
import { TilesSkeletonComponent } from "./loading-text-skeleton/tiles-skeleton/tiles-skeleton.component";
import { DetailViewComponent } from "./master-detail/detail/detail-view.component";
import { MasterDetailViewComponent } from "./master-detail/master-detail-view.component";
import { MasterViewComponent } from "./master-detail/master/master-view.component";
import { NoValuePipeModule } from "./no-value/no-value.pipe.module";
import { PageContentComponent } from "./page-content/page-content.component";
import { ProductInfoComponent } from "./product-info/product-info.component";
import { ResizeHandleComponent } from "./resize-handle/resize-handle.component";
import { BCI_SHARED_INTL_PROVIDER } from "./shared-intl";
import { SidePanelItemComponent } from "./side-panel-item/side-panel-item.component";
import { SidePanelComponent } from "./side-panel/side-panel.component";
import { TableContainerComponent } from "./table-container/table-container.component";
import { TableStripedHorizontalDirective } from "./table-striped-horizontal/table-striped-horizontal.directive";
import { BreadcrumbsModule } from "./breadcrumbs/breadcrumbs.module";

registerLocaleData(ptBr);

@NgModule({
  imports: [
    BreadcrumbsModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    /* Angular Material */
    MatCardModule,
    MatChipsModule,
    MatButtonModule,
    MatSidenavModule,
    MatToolbarModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatDialogModule,
    MatListModule,
    MatExpansionModule,
    MatBadgeModule,
    /* CDK */
    OverlayModule,
    NoValuePipeModule
  ],
  declarations: [
    FilterPanelComponent,
    SidePanelComponent,
    SidePanelItemComponent,
    ContentSidebarComponent,
    ContentSidebarItemComponent,
    ContentWSidePanelComponent,
    LoadingSpinnerDirective,
    LoadingTextSkeletonDirective,
    BciChipComponent,
    CommandbarItemComponent,
    CommandbarButtonComponent,
    CommandbarButtonsDirective,
    CommandbarComponent,
    CommandbarActionsComponent,
    CommandbarInlinefiltersComponent,
    CommandbarInlinefilterComponent,
    CommandbarQuickfiltersComponent,
    CommandbarSidebarComponent,
    CommandbarSidebarSlideComponent,
    CommandbarSidebarSlideHeaderDirective,
    CommandbarSidebarSlideFooterDirective,
    CommandbarSidebarRoutingNavigatorDirective,
    ToggleButtonDirective,
    ClickOutsideDirective,
    CommandbarActiveFiltersComponent,
    MasterDetailViewComponent,
    MasterViewComponent,
    DetailViewComponent,
    FilterPickerComponent,
    FilterEntryComponent,
    PageContentComponent,
    CommandbarSidebarSlideComponent,
    ClickableItemDirective,
    CommandbarSidebarRoutingNavigatorDirective,
    ProductInfoComponent,
    TableContainerComponent,
    /* Flex Layout */
    TableSkeletonComponent,
    TilesSkeletonComponent,
    ListSkeletonComponent,
    ResizeHandleComponent,
    CommandbarOptionsmenuComponent,
    OptionsmenuDialogComponent,
    ActivityIndicatorDirective,
    TableStripedHorizontalDirective
  ],
  exports: [
    CommonModule,
    BreadcrumbsModule,
    FormsModule,
    BciChipComponent,
    FilterPanelComponent,
    SidePanelComponent,
    SidePanelItemComponent,
    ContentSidebarComponent,
    ContentSidebarItemComponent,
    ContentWSidePanelComponent,
    LoadingSpinnerDirective,
    LoadingTextSkeletonDirective,
    MasterDetailViewComponent,
    MasterViewComponent,
    DetailViewComponent,
    CommandbarItemComponent,
    CommandbarButtonComponent,
    CommandbarButtonsDirective,
    CommandbarComponent,
    CommandbarActionsComponent,
    CommandbarInlinefiltersComponent,
    CommandbarInlinefilterComponent,
    CommandbarQuickfiltersComponent,
    CommandbarSidebarComponent,
    CommandbarSidebarSlideComponent,
    CommandbarSidebarSlideHeaderDirective,
    CommandbarSidebarSlideFooterDirective,
    CommandbarSidebarRoutingNavigatorDirective,
    ToggleButtonDirective,
    ClickOutsideDirective,
    CommandbarActiveFiltersComponent,
    FilterPickerComponent,
    FilterEntryComponent,
    PageContentComponent,
    ClickableItemDirective,
    ProductInfoComponent,
    TableSkeletonComponent,
    TilesSkeletonComponent,
    ListSkeletonComponent,
    ResizeHandleComponent,
    CommandbarOptionsmenuComponent,
    OptionsmenuDialogComponent,
    ActivityIndicatorDirective,
    NoValuePipeModule,
    /* Angular Material */
    MatCardModule,
    MatButtonModule,
    MatSidenavModule,
    /* Flex Layout */
    TableStripedHorizontalDirective,
    TableContainerComponent
  ],
  providers: [
    BCI_SHARED_INTL_PROVIDER,
    {
      provide: COMMANDBAR_OPTIONSMENU_DATA,
      useValue: {}
    }
  ]
})
export class BciSharedModule {}
